import { Box, Flex, Grid, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import {
  LuMapPin as MapPin,
  LuArrowRight as ArrowRight,
  LuFlag as Flag,
  LuTruck as Truck,
  LuFootprints as Footprints,
  LuRoute as Route,
} from 'react-icons/lu'
import type { ShipmentDetailResponse } from '../../../../api/models/response'
import { themeColors } from '../../../../theme'
import { parseUtcDate } from '../../../../lib/dates'
import { getShipmentState } from '../../../../lib/shipmentStates'
import { formatCarbonKg } from '../../../../lib/carbonFootprint'
import { CARD_SHADOW } from './cardStyles'

const MotionBox = motion.create(Box)
const colors = themeColors

function formatDate(iso: string | null): string {
  if (!iso) return '-'
  return parseUtcDate(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })
}
function formatTime(iso: string | null): string {
  if (!iso) return ''
  return parseUtcDate(iso).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
}

export default function TripInfoCard({ shipment }: { shipment: ShipmentDetailResponse }) {
  const stateInfo = getShipmentState(shipment.shipmentState)

  const detailItems: { label: string; value: string }[] = [
    { label: 'Conductor', value: shipment.driverName || '-' },
    { label: 'Placa', value: shipment.vehiclePlate || '-' },
    { label: 'Mineral', value: shipment.mineralTypeName || '-' },
    {
      label: 'Peso',
      value: shipment.mineralWeight
        ? `${shipment.mineralWeight} ${shipment.measurementUnitName || ''}`.trim()
        : '-',
    },
    { label: 'Guía Despacho', value: shipment.dispatchGuideNumber || '-' },
    { label: 'Guía Transporte', value: shipment.transportGuideNumber || '-' },
    {
      label: 'Inicio',
      value: shipment.startedAt ? `${formatDate(shipment.startedAt)} ${formatTime(shipment.startedAt)}` : '-',
    },
    {
      label: 'Fin',
      value: shipment.completedAt ? `${formatDate(shipment.completedAt)} ${formatTime(shipment.completedAt)}` : '-',
    },
  ]

  return (
    <MotionBox
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor={colors.border}
      boxShadow={CARD_SHADOW}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Banner de identidad */}
      <Box
        position="relative"
        px={{ base: 5, md: 7 }}
        py={{ base: 5, md: 6 }}
        background="linear-gradient(135deg, #012d1d 0%, #b4532a 60%, #14532d 100%)"
        overflow="hidden"
      >
        {/* Icono decorativo (marca de agua, fuera de la zona de los badges) */}
        <Box
          position="absolute"
          bottom="-46px"
          left="42%"
          opacity={0.06}
          color="white"
          pointerEvents="none"
        >
          <Route size={150} />
        </Box>

        <Flex position="relative" justifyContent="space-between" alignItems="flex-start" gap={4} flexWrap="wrap">
          <Box minW={0}>
            <Text
              fontSize="10px"
              textTransform="uppercase"
              letterSpacing="0.25em"
              fontWeight="bold"
              color="rgba(255,255,255,0.55)"
              mb={1}
            >
              Lote de traslado
            </Text>
            <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800" color="white" fontFamily="heading" lineHeight="1.1">
              {shipment.batchCode}
            </Text>
          </Box>

          <Flex direction="column" alignItems="flex-end" gap={2} flexShrink={0}>
            <Flex
              alignItems="center"
              gap={2}
              bg="rgba(255,255,255,0.18)"
              backdropFilter="blur(6px)"
              px={3}
              py={1.5}
              borderRadius="full"
              border="1px solid rgba(255,255,255,0.25)"
            >
              <Box w="8px" h="8px" borderRadius="full" bg={stateInfo.color} boxShadow={`0 0 0 3px ${stateInfo.color}55`} />
              <Text fontSize="11px" fontWeight="700" color="white" textTransform="uppercase" letterSpacing="0.06em">
                {stateInfo.label}
              </Text>
            </Flex>
            <Flex
              alignItems="center"
              gap={1.5}
              bg="rgba(255,255,255,0.12)"
              backdropFilter="blur(6px)"
              px={2.5}
              py={1}
              borderRadius="full"
              border="1px solid rgba(255,255,255,0.18)"
            >
              <Footprints size={13} color="#fcd34d" />
              <Text fontSize="11px" fontWeight="700" color="white">
                {formatCarbonKg(shipment.carbonKg)}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {/* Ruta origen → destino */}
        <Flex
          position="relative"
          alignItems="center"
          gap={3}
          mt={5}
          flexWrap="wrap"
          bg="rgba(255,255,255,0.1)"
          backdropFilter="blur(4px)"
          border="1px solid rgba(255,255,255,0.14)"
          borderRadius="xl"
          px={4}
          py={3}
        >
          <Flex alignItems="center" gap={2} minW={0}>
            <MapPin size={16} color="#86efac" />
            <Box minW={0}>
              <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.15em" color="rgba(255,255,255,0.5)" fontWeight="bold">
                Origen
              </Text>
              <Text fontSize="sm" fontWeight="700" color="white" truncate maxW="220px">
                {shipment.supplierName || '-'}
              </Text>
            </Box>
          </Flex>

          <Flex flex="1" alignItems="center" gap={2} minW="60px" color="rgba(255,255,255,0.4)">
            <Box flex="1" h="1px" bg="rgba(255,255,255,0.2)" />
            <Truck size={15} />
            <ArrowRight size={15} />
            <Box flex="1" h="1px" bg="rgba(255,255,255,0.2)" />
          </Flex>

          <Flex alignItems="center" gap={2} minW={0}>
            <Flag size={16} color="#fcd34d" />
            <Box minW={0}>
              <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.15em" color="rgba(255,255,255,0.5)" fontWeight="bold">
                Destino
              </Text>
              <Text fontSize="sm" fontWeight="700" color="white" truncate maxW="220px">
                {shipment.collectionCenterName || '-'}
              </Text>
            </Box>
          </Flex>
        </Flex>
      </Box>

      {/* Detalle */}
      <Box bg={colors.bgPanel} px={{ base: 5, md: 7 }} py={5}>
        <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4}>
          {detailItems.map((item) => (
            <Box key={item.label}>
              <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.12em" fontWeight="bold" color={colors.textMuted}>
                {item.label}
              </Text>
              <Text fontSize="sm" fontWeight="600" color={colors.text} mt={0.5} truncate>
                {item.value}
              </Text>
            </Box>
          ))}
        </Grid>
      </Box>
    </MotionBox>
  )
}
